import React from "react";
import { useSelector } from "react-redux";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { useParentStation } from "src/modules/parent-station/ParentStation/Context";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";
import { Lottie } from "src/components/Lottie";
import IconOnBackgound from "src/components/IconOnBackground";
import IconBackground from "src/atoms/IconBackground";
import { ReactComponent as PlayIcon } from "src/modules/lullaby/icons/play.svg";
import { ReactComponent as StopIcon } from "src/modules/lullaby/icons/stop.svg";
import dotsAnimation from "src/modules/lullaby/animations/dots.json";
import Controller from "./ControllerEnum";

function PlayerButton({
  onClick,
  icon,
  bg = "primary",
  fill = "white",
  ...rest
}) {
  return (
    <IconOnBackgound
      bg={bg}
      icon={icon}
      fill={fill}
      iconSize="28px"
      size="56px"
      onClick={onClick}
      {...rest}
    />
  );
}
function Animation({ onComplete, loop, segments }) {
  return (
    <IconBackground size="56px" bg="white">
      <Lottie
        width="28px"
        height="28px"
        animationData={dotsAnimation}
        eventListeners={[{ event: "complete", callback: onComplete }]}
        options={{ loop }}
        controls={{ segments }}
        browserProps={{
          hasRawData: true
        }}
      />
    </IconBackground>
  );
}

function PlayButton({ onClick, bg, fill }) {
  return <PlayerButton icon={PlayIcon} onClick={onClick} bg={bg} fill={fill} />;
}
function StopButton({ onClick, bg, fill }) {
  return <PlayerButton icon={StopIcon} onClick={onClick} bg={bg} fill={fill} />;
}

function AudioControllers({
  currentController,
  onPlay,
  onStop,
  withoutLoadingIntoStopCompleteHandler,
  initialLoadCompeleteHandler,
  loadingIntoStopCompleteHandler,
  intoPlayCompleteHandler
}) {
  const parentStation = useParentStation();
  const isFreeVersion = useSelector(isFreemium);
  const purchase = usePurchaseContext();
  switch (currentController) {
    case Controller.WITHOUT_LOADING_INTO_STOP: {
      return (
        <Animation
          key={Controller.WITHOUT_LOADING_INTO_STOP}
          loop={false}
          onComplete={withoutLoadingIntoStopCompleteHandler}
          segments={[1, 60]}
        />
      );
    }
    case Controller.INITIAL_LOAD: {
      return (
        <Animation
          key={Controller.INITIAL_LOAD}
          loop={false}
          onComplete={initialLoadCompeleteHandler}
          segments={[1, 40]}
        />
      );
    }
    case Controller.STUCK_ANIMATION: {
      return (
        <Animation
          key={Controller.STUCK_ANIMATION}
          loop={true}
          segments={[20, 40]}
        />
      );
    }
    case Controller.LOADING_INTO_STOP: {
      return (
        <Animation
          key={Controller.LOADING_INTO_STOP}
          loop={false}
          onComplete={loadingIntoStopCompleteHandler}
          segments={[40, 60]}
        />
      );
    }
    case Controller.STOP_BUTTON: {
      return <StopButton bg="white" fill="primary" onClick={onStop} />;
    }
    case Controller.INTO_PLAY: {
      return (
        <Animation
          key={Controller.INTO_PLAY}
          loop={false}
          onComplete={intoPlayCompleteHandler}
          segments={[80, 140]}
        />
      );
    }
    case Controller.PLAY_BUTTON:
    default: {
      return (
        <PlayButton
          onClick={() =>
            isFreeVersion
              ? purchase.open({
                  content: "playAndRecord",
                  props: {
                    isMonitoring: () =>
                      parentStation.primaryStation.status === "connectig" ||
                      parentStation.primaryStation.status === "connected",
                    stopMonitoring: parentStation.stopMonitoringForAll
                  }
                })
              : onPlay()
          }
        />
      );
    }
  }
}

export default React.memo(AudioControllers);
